import { Component, type ErrorInfo, type ReactNode } from 'react'
import { ErrorState } from '@/components/common/ErrorState'
import { PageSkeleton } from '@/components/common/LoadingSkeleton'
import {
  canAutoReloadForChunk,
  isChunkLoadError,
  recoverFromStaleChunk,
  reloadLatestVersion,
} from '@/utils/chunk-recovery'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  error: Error | null
  /** Stale-chunk auto reload under way — show the loader, not an error. */
  reloading: boolean
}

/** Catch unexpected render errors — wrap routes / heavy panels */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, reloading: false }

  static getDerivedStateFromError(error: Error): State {
    return { error, reloading: isChunkLoadError(error) && canAutoReloadForChunk() }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (recoverFromStaleChunk(error)) return
    // Future: report to observability
    console.error('[ErrorBoundary]', error, info.componentStack)
  }

  render() {
    if (this.state.error) {
      if (this.state.reloading) {
        return (
          <div className="flex min-h-svh items-center justify-center bg-background p-6">
            <div className="w-full max-w-md space-y-3 text-center">
              <PageSkeleton />
              <p className="text-sm text-muted-foreground">Loading the latest version…</p>
            </div>
          </div>
        )
      }

      // Auto reload already used (or blocked) — never leave a loader up forever.
      if (isChunkLoadError(this.state.error)) {
        return (
          <ErrorState
            title="Page didn't load"
            description="Check your connection, then reload."
            code="NETWORK"
            actionLabel="Reload"
            onAction={reloadLatestVersion}
            className="m-4 sm:m-6"
          />
        )
      }

      if (this.props.fallback) return this.props.fallback
      return (
        <ErrorState
          title="Unexpected error"
          description={this.state.error.message || 'The page failed to render.'}
          actionLabel="Reload page"
          onAction={() => window.location.reload()}
          className="m-4 sm:m-6"
        />
      )
    }
    return this.props.children
  }
}
